import { BrowserFeatureError, MatchTransportError } from "./errors";
import type { PingMessage, PongMessage, ReadyMessage } from "./protocol";
import { LOGICAL_TICK_RATE } from "./protocol";

export const DEFAULT_CLOCK_SAMPLE_COUNT = 20;
export const MINIMUM_START_LEAD_MS = 3_000;
export const DEFAULT_START_LEAD_MS = 3_500;

export function monotonicNow(): number {
  if (typeof performance === "undefined" || typeof performance.now !== "function") {
    throw new BrowserFeatureError(
      "PerformanceClock",
      "a monotonic performance.now() clock is required for a match",
    );
  }
  return performance.now();
}

export interface ClockSample {
  readonly pingId: string;
  /** Estimated remote monotonic clock minus local monotonic clock. */
  readonly offsetMs: number;
  readonly roundTripMs: number;
  readonly remoteProcessingMs: number;
  readonly localReceiveMs: number;
}

export interface ClockEstimate {
  /** Add this value to local time to express it on the remote clock. */
  readonly remoteMinusLocalMs: number;
  readonly medianRoundTripMs: number;
  readonly jitterMs: number;
  readonly sampleCount: number;
  readonly retainedSampleCount: number;
  readonly stable: boolean;
}

function median(values: readonly number[]): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

/**
 * NTP-style clock sampler. It keeps the lowest-RTT 60% before taking a
 * median offset, which rejects queueing spikes without trusting one sample.
 */
export class ClockSynchronizer {
  readonly targetSampleCount: number;
  private readonly now: () => number;
  private readonly pending = new Map<string, number>();
  private readonly collected: ClockSample[] = [];
  private pingCounter = 0;

  constructor(options: {
    readonly targetSampleCount?: number;
    readonly now?: () => number;
  } = {}) {
    const target = options.targetSampleCount ?? DEFAULT_CLOCK_SAMPLE_COUNT;
    if (!Number.isSafeInteger(target) || target < 5 || target > 100) {
      throw new RangeError("Clock sample count must be an integer between 5 and 100");
    }
    this.targetSampleCount = target;
    this.now = options.now ?? monotonicNow;
  }

  createPing(nowMs = this.now()): PingMessage {
    const pingId = `${++this.pingCounter}-${Math.round(nowMs * 1_000)}`;
    this.pending.set(pingId, nowMs);
    return { type: "ping", pingId, localSendMs: nowMs };
  }

  createPong(ping: PingMessage, remoteReceiveMs = this.now()): PongMessage {
    return {
      type: "pong",
      pingId: ping.pingId,
      localSendMs: ping.localSendMs,
      remoteReceiveMs,
      remoteSendMs: this.now(),
    };
  }

  acceptPong(pong: PongMessage, localReceiveMs = this.now()): ClockSample | undefined {
    const localSendMs = this.pending.get(pong.pingId);
    if (localSendMs === undefined) return undefined;
    this.pending.delete(pong.pingId);

    const remoteProcessingMs = Math.max(0, pong.remoteSendMs - pong.remoteReceiveMs);
    const roundTripMs = Math.max(
      0,
      localReceiveMs - localSendMs - remoteProcessingMs,
    );
    const offsetMs =
      (pong.remoteReceiveMs - localSendMs + pong.remoteSendMs - localReceiveMs) / 2;
    if (!Number.isFinite(offsetMs) || !Number.isFinite(roundTripMs)) return undefined;

    const sample: ClockSample = {
      pingId: pong.pingId,
      offsetMs,
      roundTripMs,
      remoteProcessingMs,
      localReceiveMs,
    };
    this.collected.push(sample);
    // Keep diagnostics bounded if a session remains open for many rounds.
    if (this.collected.length > 200) this.collected.splice(0, this.collected.length - 200);
    return sample;
  }

  get sampleCount(): number {
    return this.collected.length;
  }

  get complete(): boolean {
    return this.sampleCount >= this.targetSampleCount;
  }

  get samples(): readonly ClockSample[] {
    return [...this.collected];
  }

  clear(): void {
    this.pending.clear();
    this.collected.length = 0;
  }

  estimate(minimumSamples = 5): ClockEstimate {
    if (this.collected.length < minimumSamples) {
      throw new MatchTransportError(
        "clock",
        `Need at least ${minimumSamples} clock samples; have ${this.collected.length}`,
        { recoverable: true },
      );
    }
    const byRoundTrip = [...this.collected].sort(
      (left, right) => left.roundTripMs - right.roundTripMs,
    );
    const retainedCount = Math.max(
      minimumSamples,
      Math.ceil(byRoundTrip.length * 0.6),
    );
    const retained = byRoundTrip.slice(0, retainedCount);
    const offset = median(retained.map((sample) => sample.offsetMs));
    const medianAbsoluteDeviation = median(
      retained.map((sample) => Math.abs(sample.offsetMs - offset)),
    );
    const jitterMs = medianAbsoluteDeviation * 1.4826;
    const medianRoundTripMs = median(retained.map((sample) => sample.roundTripMs));
    return {
      remoteMinusLocalMs: offset,
      medianRoundTripMs,
      jitterMs,
      sampleCount: this.collected.length,
      retainedSampleCount: retained.length,
      stable: medianRoundTripMs <= 150 && jitterMs <= 50,
    };
  }
}

export interface StartProposalOptions {
  readonly coordinatorId: string;
  readonly manifestHash: string;
  readonly nowMs: number;
  readonly startTick?: number;
  readonly leadTimeMs?: number;
  readonly tickRate?: number;
  readonly proposalId?: string;
}

/** Creates a proposal aligned to a logical tick and at least three seconds out. */
export function createStartProposal(options: StartProposalOptions): ReadyMessage {
  const leadTimeMs = Math.max(
    MINIMUM_START_LEAD_MS,
    options.leadTimeMs ?? DEFAULT_START_LEAD_MS,
  );
  const tickRate = options.tickRate ?? LOGICAL_TICK_RATE;
  if (!options.coordinatorId || !options.manifestHash || !Number.isFinite(options.nowMs)) {
    throw new MatchTransportError("clock", "Invalid start proposal fields");
  }
  if (!Number.isFinite(tickRate) || tickRate <= 0) {
    throw new RangeError("tickRate must be positive");
  }
  const tickDurationMs = 1_000 / tickRate;
  const startTimeMs =
    Math.ceil((options.nowMs + leadTimeMs) / tickDurationMs) * tickDurationMs;
  const startTick = options.startTick ?? 0;
  const proposalId =
    options.proposalId ??
    `${options.coordinatorId}:${startTick}:${Math.round(startTimeMs * 1_000)}`;
  return {
    type: "ready",
    manifestHash: options.manifestHash,
    startTick,
    startTimeMs,
    coordinatorId: options.coordinatorId,
    proposalId,
    agreement: "propose",
  };
}

export interface AcceptedStart {
  readonly response: ReadyMessage;
  readonly localStartTimeMs: number;
}

/** Convert a time on the remote clock to the local monotonic clock. */
export function remoteTimeToLocalTime(
  remoteTimeMs: number,
  remoteMinusLocalMs: number,
): number {
  return remoteTimeMs - remoteMinusLocalMs;
}

/**
 * Validates a remote proposal against the measured clock and returns the exact
 * acceptance echo. The default proposal lead (3.5 s) leaves RTT headroom while
 * enforcing a full 3 s minimum when the proposal is made.
 */
export function acceptStartProposal(options: {
  readonly proposal: ReadyMessage;
  readonly expectedManifestHash: string;
  readonly remoteCoordinatorId: string;
  readonly estimate: ClockEstimate;
  readonly localNowMs: number;
  readonly minimumRemainingLeadMs?: number;
}): AcceptedStart {
  const { proposal } = options;
  if (
    proposal.agreement !== "propose" ||
    proposal.coordinatorId !== options.remoteCoordinatorId ||
    proposal.manifestHash !== options.expectedManifestHash
  ) {
    throw new MatchTransportError("handshake", "Start proposal does not match this peer");
  }
  const localStartTimeMs = remoteTimeToLocalTime(
    proposal.startTimeMs,
    options.estimate.remoteMinusLocalMs,
  );
  const minimumRemaining = options.minimumRemainingLeadMs ?? MINIMUM_START_LEAD_MS;
  if (localStartTimeMs - options.localNowMs < minimumRemaining) {
    throw new MatchTransportError(
      "clock",
      "Start proposal is not far enough in the future",
      { recoverable: true },
    );
  }
  return {
    response: { ...proposal, agreement: "accept" },
    localStartTimeMs,
  };
}

export function isExactStartAcceptance(
  proposal: ReadyMessage,
  response: ReadyMessage,
): boolean {
  return (
    proposal.agreement === "propose" &&
    response.agreement === "accept" &&
    proposal.manifestHash === response.manifestHash &&
    proposal.startTick === response.startTick &&
    proposal.startTimeMs === response.startTimeMs &&
    proposal.coordinatorId === response.coordinatorId &&
    proposal.proposalId === response.proposalId
  );
}

export class SharedGameClock {
  readonly localStartTimeMs: number;
  readonly startTick: number;
  readonly tickRate: number;
  private readonly now: () => number;

  constructor(options: {
    readonly localStartTimeMs: number;
    readonly startTick?: number;
    readonly tickRate?: number;
    readonly now?: () => number;
  }) {
    this.localStartTimeMs = options.localStartTimeMs;
    this.startTick = options.startTick ?? 0;
    this.tickRate = options.tickRate ?? LOGICAL_TICK_RATE;
    this.now = options.now ?? monotonicNow;
    if (!Number.isFinite(this.localStartTimeMs) || this.tickRate <= 0) {
      throw new RangeError("Invalid shared game clock configuration");
    }
  }

  tickAt(localTimeMs: number): number {
    if (localTimeMs <= this.localStartTimeMs) return this.startTick;
    return (
      this.startTick +
      Math.floor(((localTimeMs - this.localStartTimeMs) * this.tickRate) / 1_000)
    );
  }

  nowTick(): number {
    return this.tickAt(this.now());
  }

  localTimeAtTick(tick: number): number {
    return this.localStartTimeMs + ((tick - this.startTick) * 1_000) / this.tickRate;
  }
}
